var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '../../theme';
import { useMultipleMemoClasses, useMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    base: {
        margin: 'var(--RUI-margin)',
        display: 'inline-block',
    },
    baseOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.base)); },
    switchContainer: {
        position: 'relative',
        display: 'inline-block',
        width: '40px',
        height: '20px',
    },
    switchContainerOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.switchContainer)); },
    slider: {
        position: 'absolute',
        cursor: 'pointer',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: '#ccc',
        transition: '.4s',
        '&::before': {
            boxSizing: 'border-box',
            position: 'absolute',
            content: '""',
            height: '80%',
            width: '40%',
            left: '5%',
            bottom: '10%',
            backgroundColor: 'white',
            transition: '0.4s',
        },
    },
    sliderOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.slider)); },
    input: {
        opacity: 0,
        width: 0,
        height: 0,
        '&:checked + $slider::before': {
            transform: 'translateX(125%)',
        },
        '&:checked + $slider': {
            backgroundColor: 'var(--RUI-switch-color)',
        },
        '&:checked:disabled + $slider': {
            backgroundColor: 'var(--RUI-switch-color-dark)',
        },
        '&:focus + $slider': {
            boxShadow: '0 0 1px var(--RUI-switch-color)',
        },
    },
    inputOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.input)); },
    small: {
        width: '20px',
        height: '10px',
    },
    smallOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.small)); },
    big: {
        width: '80px',
        height: '40px',
    },
    bigOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.big)); },
    primary: {
        '--RUI-switch-color': 'var(--RUI-color-primary)',
        '--RUI-switch-color-dark': 'var(--RUI-color-primary-dark)',
    },
    secondary: {
        '--RUI-switch-color': 'var(--RUI-color-secondary)',
        '--RUI-switch-color-dark': 'var(--RUI-color-secondary-dark)',
    },
    neutral: {
        '--RUI-switch-color': 'var(--RUI-color-neutral)',
        '--RUI-switch-color-dark': 'var(--RUI-color-neutral-dark)',
    },
    error: {
        '--RUI-switch-color': 'var(--RUI-color-error)',
        '--RUI-switch-color-dark': 'var(--RUI-color-error-dark)',
    },
    success: {
        '--RUI-switch-color': 'var(--RUI-color-success)',
        '--RUI-switch-color-dark': 'var(--RUI-color-success-dark)',
    },
    warning: {
        '--RUI-switch-color': 'var(--RUI-color-warning)',
        '--RUI-switch-color-dark': 'var(--RUI-color-warning-dark)',
    },
    roundOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.round)); },
    round: {
        borderRadius: '34px',
        '&::before': {
            borderRadius: '50%',
        },
    },
    label: {
        marginLeft: 'calc(0.5 * var(--RUI-margin))',
    },
    labelOverrides: function (theme) { return (__assign({}, theme.overrides.Switch.label)); },
}, { name: 'RUISwitch' });
var Switch = function (_a) {
    var color = _a.color, size = _a.size, rounded = _a.rounded, className = _a.className, style = _a.style, label = _a.label, id = _a.id, props = __rest(_a, ["color", "size", "rounded", "className", "style", "label", "id"]);
    var theme = useTheme();
    var styles = useStyles(theme);
    var switchContainerClasses = useMemoClasses([
        styles.switchContainer,
        theme.overrides.Switch.switchContainer && styles.switchContainerOverrides,
        styles[color],
        size === 'small' && styles.small,
        size === 'small' && theme.overrides.Switch.small && styles.smallOverrides,
        size === 'big' && styles.big,
        size === 'big' && theme.overrides.Switch.big && styles.bigOverrides,
        'RUISwitch-switchContainer',
    ], [theme, size, color]);
    var sliderClasses = useMemoClasses([
        styles.slider,
        theme.overrides.Switch.slider && styles.sliderOverrides,
        rounded && styles.round,
        theme.overrides.Switch.round && styles.roundOverrides,
        'RUISwitch-slider',
    ], [theme, rounded]);
    var _b = useMultipleMemoClasses([
        [styles.input, theme.overrides.Switch.input && styles.inputOverrides, 'RUISwitch-input'],
        [styles.label, theme.overrides.Switch.label && styles.labelOverrides, 'RUISwitch-label'],
    ], [theme]), inputClasses = _b[0], labelClasses = _b[1];
    var baseClasses = useMemoClasses([styles.base, theme.overrides.Switch.base && styles.baseOverrides, 'RUISwitch-base', className], [theme, className]);
    return (React.createElement("div", { className: baseClasses, style: style },
        React.createElement("label", { className: switchContainerClasses, htmlFor: id },
            React.createElement("input", __assign({ className: inputClasses, type: "checkbox", id: id }, props)),
            React.createElement("span", { className: sliderClasses })),
        label && React.createElement("span", { className: labelClasses }, label)));
};
Switch.defaultProps = {
    color: 'primary',
    size: 'normal',
    label: '',
    rounded: true,
};
export { Switch as default, Switch };
